import { fingerprintValue } from './quote-snapshot-diff';
import { selectRequiredProjectPhotos } from './project-photo-contract';
import { contentProvenanceMatches } from './source-provenance';

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : {};
}

function list(value: unknown): UnknownRecord[] {
    return (Array.isArray(value) ? value : []).map(record);
}

function textFingerprint(value: unknown): string {
    const text = String(value || '');
    return text ? fingerprintValue(text) : '';
}

/**
 * Project Summary writes its own scope objective into jobDescription. That
 * output must not immediately stale the summary that produced it, but a
 * manual value (or an edit whose old marker no longer matches) is real prompt
 * evidence. Only the current content-bound AI marker earns that exclusion.
 */
function promptOwnedJobDescription(form: UnknownRecord): string {
    const value = String(form.jobDescription || '');
    const provenance = record(form.jobDescriptionProvenance);
    const isExactAiOutput = provenance.source === 'ai-project-summary'
        && provenance.version === 1
        && typeof provenance.contentFingerprint === 'string'
        && provenance.contentFingerprint.length > 0
        && contentProvenanceMatches(provenance, value);
    return isExactAiOutput ? '' : value;
}

/**
 * Return the exact form projection Project Summary is allowed to consume.
 *
 * The summary writes its own scope objective back to `jobDescription`. Feeding
 * that value into the next summary prompt while excluding it from the evidence
 * fingerprint would let prompt input change behind an apparently fresh durable
 * receipt. Keep the prompt and its freshness identity congruent by blanking
 * only a content-bound value that is still provably owned by Project Summary.
 * A manual value, operator provenance, or a stale/mismatched AI marker remains
 * prompt evidence.
 */
export function buildProjectSummaryPromptFormData(value: unknown): UnknownRecord {
    const form = record(value);
    return {
        ...form,
        jobDescription: promptOwnedJobDescription(form),
    };
}

function documentPromptEvidence(value: unknown): UnknownRecord[] {
    // condenseDocuments consumes every document and preserves order within its
    // RFQ/non-RFQ stable groups; fetchDeepContext also consumes the first three
    // in caller order. Never sort this receipt independently of those paths.
    return list(value).map((item) => {
        // The visible context prefers non-blank `content`, while retrieval
        // currently prefers `text`. Capture both so neither prompt path can
        // change behind an apparently fresh receipt.
        const content = typeof item.content === 'string' ? item.content : '';
        const text = typeof item.text === 'string' ? item.text : '';
        return {
            id: String(item.id || item.blobId || item.fileMetadataId || ''),
            name: String(item.name || item.originalName || item.fileName || ''),
            class: String(item.class || item.type || item.aiClassification || ''),
            size: Number(item.size || item.sizeBytes || item.sizeKB || 0),
            extractionFailed: item.extractionFailed === true,
            contentIncomplete: item.contentComplete === false,
            contentLength: content.length,
            contentFingerprint: textFingerprint(content),
            textLength: text.length,
            textFingerprint: textFingerprint(text),
        };
    });
}

function photoPromptEvidence(value: unknown): UnknownRecord {
    const photos = selectRequiredProjectPhotos(value);
    // buildSummaryContext exposes total count, the first 30 rows, and the
    // number omitted. Rows after 30 therefore affect only the count.
    return {
        count: photos.length,
        rows: photos.slice(0, 30).map((item, index) => ({
            id: String(item.id || item.blobId || item.fileMetadataId || ''),
            name: String(item.name || item.filename || `photo_${index + 1}`),
            label: String(item.label || item.aiLabel || item.description || ''),
            sizeKB: item.sizeKB ? String(item.sizeKB) : '',
        })),
    };
}

function videoTranscriptPromptEvidence(value: unknown): UnknownRecord[] {
    return list(value).slice(0, 5).map((item, index) => {
        const text = String(item.text || item.transcript || '').substring(0, 2000);
        return {
            name: String(item.name || item.filename || `video_${index + 1}`),
            textLength: text.length,
            textFingerprint: textFingerprint(text),
        };
    });
}

/**
 * Deterministic identity of the exact Project Summary evidence projection.
 * Provider/model/key state is execution state and belongs in the attempt key,
 * not in this durable evidence receipt.
 *
 * The preprocessed-file lane is NOT Summary evidence, and this function does
 * not accept it. That lane is a transient import product: the project record
 * never stores it, so the Summary (which reads the prepared record) always got
 * an empty lane while the screen still held the import's entries. Every saved
 * Summary then read as stale on arrival, its Risk handoff was refused and the
 * automatic trigger paid for the Summary again every two seconds (2026-10-21,
 * an .eml plus a text PDF with nothing for evidence preparation to adopt).
 * A reload also empties the lane, so no fingerprint that includes it can be
 * recomputed for a reopened project; Risk freshness leaves it out for the same
 * reason (risk-evidence-freshness.ts). The hashed object keeps an empty lane so
 * every receipt saved since v6.0.15, all built with an empty lane, keeps its
 * identity.
 */
export function buildProjectSummaryEvidenceFingerprint({
    formData,
    documents,
    photos,
    videoTranscripts,
}: {
    formData?: unknown;
    documents?: unknown;
    photos?: unknown;
    videoTranscripts?: unknown;
} = {}): string {
    const form = buildProjectSummaryPromptFormData(formData);
    return `project-summary-evidence-v2:${fingerprintValue({
        form: {
            clientName: String(form.clientName || ''),
            clientCompany: String(form.clientCompany || ''),
            clientEmail: String(form.clientEmail || ''),
            clientPhone: String(form.clientPhone || ''),
            siteContactName: String(form.siteContactName || ''),
            siteContactPhone: String(form.siteContactPhone || ''),
            address: String(form.address || ''),
            unitNumber: String(form.unitNumber || ''),
            strataPlan: String(form.strataplan || form.strataPlan || form.strata_plan || ''),
            orderNumber: String(form.orderNumber || form.confirmedOrderNumber || ''),
            assessor: String(form.assessor || ''),
            visitDate: String(form.visitDate || ''),
            jobDescription: String(form.jobDescription || ''),
            notes: String(form.notes || form.inspectorNotes || ''),
            rfqContent: String(form.rfqContent || ''),
        },
        documents: documentPromptEvidence(documents),
        photos: photoPromptEvidence(photos),
        videoTranscripts: videoTranscriptPromptEvidence(videoTranscripts),
        preprocessedFiles: [],
    })}`;
}

export function selectProjectSummaryPhotos(value: unknown): UnknownRecord[] {
    return selectRequiredProjectPhotos(value);
}
